import { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Building2, Clock, Globe, Ticket, AlertCircle, CheckCircle2, Loader2, ExternalLink, BarChart3, Calendar, Plus, Server, Shield, Key, MessageSquare, Trash2, Edit, Save, X, Eye, EyeOff, Copy, FileText, Bookmark, History, Activity, ShieldCheck, Layout, XCircle, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';

import TicketDialog from '@/components/TicketDialog';
import ProblemTypeSearch from '@/components/ProblemTypeSearch';
import RequesterSearch from '@/components/RequesterSearch';
import InfraTagSearch from '@/components/InfraTagSearch';
import CsvExportButton from '@/components/CsvExportButton';
import ClickUpTaskButton from '@/components/ClickUpTaskButton';

const BACKEND_URL = process.env.REACT_APP_API_BASE_URL;
const API = `${BACKEND_URL}/api`;

const statusColors = {
    open: 'bg-blue-100 text-blue-700',
    in_progress: 'bg-yellow-100 text-yellow-700',
    resolved: 'bg-green-100 text-green-700',
    closed: 'bg-gray-100 text-gray-700',
};

const priorityColors = {
    critical: 'bg-red-100 text-red-700 border-red-200',
    high: 'bg-orange-100 text-orange-700 border-orange-200',
    medium: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    low: 'bg-green-100 text-green-700 border-green-200',
};

const getTodayDate = () => {
    const today = new Date();
    const offset = today.getTimezoneOffset();
    return new Date(today.getTime() - offset * 60000).toISOString().slice(0, 10);
};

const CompanyDetail = ({ user }) => {
    const navigate = useNavigate();
    const { companyId } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const [dateRangeType, setDateRangeType] = useState('all');
    const [customRange, setCustomRange] = useState({ start: '', end: '' });

    const [ticketDialogOpen, setTicketDialogOpen] = useState(false);
    const [selectedTicketId, setSelectedTicketId] = useState(null);
    const [sortBy, setSortBy] = useState('newest');

    // Create Task
    const [createTaskOpen, setCreateTaskOpen] = useState(false);
    const [newTask, setNewTask] = useState({ title: '', description: '', problem_type: '', due_date: '', company_id: '', requested_by: '', assigned_to: '' });
    const [submittingTask, setSubmittingTask] = useState(false);
    const [taskAssignees, setTaskAssignees] = useState([]);

    // Maintenance Reports
    const [maintenanceDialogOpen, setMaintenanceDialogOpen] = useState(false);
    const [newReport, setNewReport] = useState({
        maintenance_date: getTodayDate(),
        plugins_status: 'healthy',
        plugins_notes: '',
        security_status: 'healthy',
        security_notes: '',
        debug_log_status: 'healthy',
        debug_log_notes: '',
        layout_status: 'healthy',
        layout_notes: '',
        general_notes: '',
        time_spent: 0
    });

    // Tab state
    const [activeTab, setActiveTab] = useState('dashboard');

    // Client Info state
    const [editingInfo, setEditingInfo] = useState(false);
    const [infoForm, setInfoForm] = useState({
        hosting_location: '', domain_registrar: '', dns_zone_editor: '',
        delegate_access: '', domain_expiry: '', credentials: [], tags: [],
        exclude_from_maintenance: false
    });
    const [savingInfo, setSavingInfo] = useState(false);

    // Knowledge Notes
    const [noteText, setNoteText] = useState('');
    const [addingNote, setAddingNote] = useState(false);

    // Credential visibility
    const [visibleCreds, setVisibleCreds] = useState(new Set());

    // Infrastructure options (distinct values from all companies)
    const [infraOptions, setInfraOptions] = useState({ hosting_locations: [], domain_registrars: [], dns_zone_editors: [] });

    // Custom company tags
    const [availableTags, setAvailableTags] = useState([]);

    // Audit log
    const [auditLogs, setAuditLogs] = useState([]);

    const PROBLEM_TYPES = [
        { value: 'site_down', label: 'Site Down/Not Loading' },
        { value: 'critical_bug', label: 'Critical Bug/Error' },
        { value: 'functionality_broken', label: 'Functionality Not Working' },
        { value: 'performance_issue', label: 'Performance/Speed Issue' },
        { value: 'content_update', label: 'Content Update Request' },
        { value: 'design_change', label: 'Design/Layout Change' },
        { value: 'feature_request', label: 'New Feature Request' },
        { value: 'other', label: 'Other' },
    ];

    const getAuthHeaders = () => ({
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
    });

    useEffect(() => {
        axios.get(`${API}/task-assignees`, getAuthHeaders())
            .then(response => setTaskAssignees(response.data || []))
            .catch(() => setTaskAssignees([]));
    }, []); // eslint-disable-line

    const getMaintenanceBadge = (status) => {
        switch (status) {
            case 'healthy': return <Badge className="bg-green-100 text-green-700 hover:bg-green-100"><CheckCircle2 className="w-3 h-3 mr-1" /> Healthy</Badge>;
            case 'issues_found': return <Badge className="bg-yellow-100 text-yellow-700 hover:bg-yellow-100"><AlertTriangle className="w-3 h-3 mr-1" /> Issues</Badge>;
            case 'critical': return <Badge className="bg-red-100 text-red-700 hover:bg-red-100"><XCircle className="w-3 h-3 mr-1" /> Critical</Badge>;
            default: return <Badge variant="outline">Not Checked</Badge>;
        }
    };

    const resetMaintenanceForm = () => {
        setNewReport({
            maintenance_date: getTodayDate(),
            plugins_status: 'healthy',
            plugins_notes: '',
            security_status: 'healthy',
            security_notes: '',
            debug_log_status: 'healthy',
            debug_log_notes: '',
            layout_status: 'healthy',
            layout_notes: '',
            general_notes: '',
            time_spent: 0
        });
    };

    const openCreateMaintenanceDialog = () => {
        resetMaintenanceForm();
        setMaintenanceDialogOpen(true);
    };

    const handleEditReport = (report) => {
        setNewReport({
            maintenance_date: report.maintenance_date || getTodayDate(),
            plugins_status: report.plugins_status,
            plugins_notes: report.plugins_notes || '',
            security_status: report.security_status,
            security_notes: report.security_notes || '',
            debug_log_status: report.debug_log_status,
            debug_log_notes: report.debug_log_notes || '',
            layout_status: report.layout_status,
            layout_notes: report.layout_notes || '',
            general_notes: report.general_notes || '',
            time_spent: report.time_spent || 0
        });
        setMaintenanceDialogOpen(true);
    };

    const handleCreateReport = async () => {
        if (!newReport.maintenance_date) {
            toast.error('Maintenance date is required');
            return;
        }
        try {
            await axios.post(`${API}/companies/${companyId}/maintenance-reports`, newReport, getAuthHeaders());
            toast.success('Maintenance report saved as In Progress');
            setMaintenanceDialogOpen(false);
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to save maintenance report');
        }
    };

    const handleDeleteReport = async (reportId) => {
        if (!window.confirm('Are you sure you want to delete this report?')) return;
        try {
            await axios.delete(`${API}/companies/${companyId}/maintenance-reports/${reportId}`, getAuthHeaders());
            toast.success('Report deleted');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to delete report');
        }
    };

    const handleCompleteReport = async (reportId) => {
        try {
            await axios.patch(`${API}/companies/${companyId}/maintenance-reports/${reportId}/complete`, {}, getAuthHeaders());
            toast.success('Maintenance completed on the company board, schedule, and ClickUp');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to complete maintenance');
        }
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            let url = `${API}/companies/${companyId}/stats`;

            if (dateRangeType !== 'all') {
                const now = new Date();
                let start = new Date();
                let end = new Date();

                if (dateRangeType === 'today') {
                    start.setHours(0, 0, 0, 0);
                    end.setHours(23, 59, 59, 999);
                } else if (dateRangeType === 'yesterday') {
                    start.setDate(now.getDate() - 1);
                    start.setHours(0, 0, 0, 0);
                    end.setDate(now.getDate() - 1);
                    end.setHours(23, 59, 59, 999);
                } else if (dateRangeType === 'this_week') {
                    const first = now.getDate() - now.getDay();
                    start = new Date(now.setDate(first));
                    start.setHours(0, 0, 0, 0);
                    end = new Date();
                } else if (dateRangeType === 'this_month') {
                    start = new Date(now.getFullYear(), now.getMonth(), 1);
                    end = new Date();
                } else if (dateRangeType === 'custom' && customRange.start && customRange.end) {
                    start = new Date(customRange.start + 'T00:00:00');
                    end = new Date(customRange.end + 'T23:59:59');
                }

                if (dateRangeType !== 'custom' || (customRange.start && customRange.end)) {
                    url += `?start_date=${start.toISOString()}&end_date=${end.toISOString()}`;
                }
            }

            const [res, auditRes] = await Promise.all([
                axios.get(url, getAuthHeaders()),
                axios.get(`${API}/audit-logs/company/${companyId}`, getAuthHeaders()).catch(() => ({ data: [] }))
            ]);
            setData(res.data);
            setAuditLogs(auditRes.data);
        } catch (error) {
            toast.error('Failed to load company data');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateTask = async (e) => {
        e.preventDefault();
        if (!newTask.problem_type) {
            toast.error("Problem Type is required");
            return;
        }
        setSubmittingTask(true);
        try {
            await axios.post(`${API}/tickets`, { ...newTask, company_id: companyId }, getAuthHeaders());
            toast.success('Task created!');
            setCreateTaskOpen(false);
            setNewTask({ title: '', description: '', problem_type: '', due_date: '', company_id: '', requested_by: '', assigned_to: '' });
            fetchData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to create task');
        } finally {
            setSubmittingTask(false);
        }
    };

    const startEditInfo = () => {
        if (!data) return;
        const c = data.company;
        setInfoForm({
            hosting_location: c.hosting_location || c.hosting || '',
            domain_registrar: c.domain_registrar || '',
            dns_zone_editor: c.dns_zone_editor || '',
            delegate_access: c.delegate_access || '',
            domain_expiry: c.domain_expiry ? c.domain_expiry.substring(0, 10) : '',
            credentials: (c.credentials || []).map(cr => ({ ...cr })),
            tags: c.tags || [],
            exclude_from_maintenance: Boolean(c.exclude_from_maintenance),
        });
        setEditingInfo(true);
    };

    const handleSaveInfo = async () => {
        setSavingInfo(true);
        try {
            const payload = { ...infoForm };
            if (payload.domain_expiry) {
                payload.domain_expiry = new Date(payload.domain_expiry + 'T00:00:00').toISOString();
            }
            await axios.patch(`${API}/companies/${companyId}/info`, payload, getAuthHeaders());
            toast.success('Client info updated');
            setEditingInfo(false);
            fetchData();
            // Refresh options so new values show in other companies
            axios.get(`${API}/companies/infra-options`, getAuthHeaders()).then(r => setInfraOptions(r.data)).catch(() => {});
        } catch { toast.error('Failed to update info'); }
        finally { setSavingInfo(false); }
    };

    const handleAddNote = async () => {
        if (!noteText.trim()) return;
        setAddingNote(true);
        try {
            await axios.post(`${API}/companies/${companyId}/knowledge-notes`, { text: noteText }, getAuthHeaders());
            setNoteText('');
            toast.success('Note added');
            fetchData();
        } catch { toast.error('Failed to add note'); }
        finally { setAddingNote(false); }
    };

    const handleDeleteNote = async (noteId) => {
        try {
            await axios.delete(`${API}/companies/${companyId}/knowledge-notes/${noteId}`, getAuthHeaders());
            toast.success('Note deleted');
            fetchData();
        } catch { toast.error('Failed to delete note'); }
    };

    const addCredential = () => {
        setInfoForm(f => ({ ...f, credentials: [...f.credentials, { id: Date.now().toString(), label: '', value: '' }] }));
    };

    const removeCredential = (idx) => {
        setInfoForm(f => ({ ...f, credentials: f.credentials.filter((_, i) => i !== idx) }));
    };

    const updateCredential = (idx, field, val) => {
        setInfoForm(f => {
            const creds = [...f.credentials];
            creds[idx] = { ...creds[idx], [field]: val };
            return { ...f, credentials: creds };
        });
    };

    const toggleCredVisible = (id) => {
        setVisibleCreds(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        toast.success('Copied to clipboard');
    };

    useEffect(() => {
        if (dateRangeType !== 'custom' || (customRange.start && customRange.end)) {
            fetchData();
        }
    }, [companyId, dateRangeType, customRange.start, customRange.end]);

    useEffect(() => {
        axios.get(`${API}/companies/infra-options`, getAuthHeaders())
            .then(r => setInfraOptions(r.data))
            .catch(() => {});
        axios.get(`${API}/companies/custom-tags`, getAuthHeaders())
            .then(r => setAvailableTags(r.data || []))
            .catch(() => {});
    }, []); // eslint-disable-line

    if (loading) return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        </div>
    );

    if (!data) return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
            <p className="text-gray-600">Company not found</p>
        </div>
    );

    const { company, total_tickets, open_tickets, in_progress_tickets, resolved_tickets, total_hours_spent, tickets, maintenance_reports = [] } = data;

    return (
        <div className="min-h-screen bg-gray-50">
            <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16">
                        <div className="flex items-center gap-3">
                            <Button variant="outline" size="sm" onClick={() => navigate('/admin/companies')} className="gap-2">
                                <ArrowLeft className="w-4 h-4" /> Companies
                            </Button>
                            <div className="h-6 w-px bg-gray-200 mx-1" />
                            <div>
                                <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                                    <Building2 className="w-5 h-5" /> {company.name}
                                </h1>
                                {company.url && (
                                    <a
                                        href={company.url.startsWith('http') ? company.url : `https://${company.url}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                                    >
                                        <ExternalLink className="w-3 h-3" /> {company.url}
                                    </a>
                                )}
                            </div>
                            {/* Custom tags */}
                            {company.tags && company.tags.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-1">
                                    {company.tags.map(tag => (
                                        <span key={tag} className="text-[10px] px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 font-medium">
                                            {tag}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                        <Button
                            onClick={() => setCreateTaskOpen(true)}
                            className="bg-black hover:bg-gray-800 gap-2"
                            size="sm"
                        >
                            <Plus className="w-4 h-4" /> Create Task
                        </Button>
                    </div>
                </div>
            </header>

            {/* Tab Navigation */}
            <div className="bg-white border-b border-gray-200 sticky top-16 z-[9]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex gap-1">
                        {[['dashboard', 'Dashboard', BarChart3], ['client-info', 'Client Info', FileText]].map(([key, label, Icon]) => (
                            <button key={key} onClick={() => setActiveTab(key)}
                                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === key ? 'border-gray-900 text-gray-900' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}>
                                <Icon className="w-4 h-4" />{label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
              {activeTab === 'dashboard' && (<>
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold text-gray-900">Dashboard</h2>
                    <div className="flex gap-2 items-center bg-white p-2 rounded-lg border shadow-sm">
                        <Calendar className="w-4 h-4 text-gray-500 ml-2" />
                        <Select value={dateRangeType} onValueChange={setDateRangeType}>
                            <SelectTrigger className="w-[180px] border-none focus:ring-0 shadow-none">
                                <SelectValue placeholder="Select timeframe" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Time</SelectItem>
                                <SelectItem value="today">Today</SelectItem>
                                <SelectItem value="yesterday">Yesterday</SelectItem>
                                <SelectItem value="this_week">This Week</SelectItem>
                                <SelectItem value="this_month">This Month</SelectItem>
                                <SelectItem value="custom">Custom Range</SelectItem>
                            </SelectContent>
                        </Select>

                        {dateRangeType === 'custom' && (
                            <div className="flex items-center gap-2 pl-2 border-l ml-2">
                                <Input
                                    type="date"
                                    className="w-auto h-8 text-xs"
                                    value={customRange.start}
                                    onChange={(e) => setCustomRange({ ...customRange, start: e.target.value })}
                                />
                                <span className="text-sm text-gray-400">to</span>
                                <Input
                                    type="date"
                                    className="w-auto h-8 text-xs"
                                    value={customRange.end}
                                    onChange={(e) => setCustomRange({ ...customRange, end: e.target.value })}
                                />
                            </div>
                        )}
                    </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 mb-8">
                    <Card className="border-gray-200">
                        <CardContent className="p-4">
                            <p className="text-xs text-gray-500 font-medium">Total Tickets</p>
                            <div className="text-2xl font-bold mt-1">{total_tickets}</div>
                        </CardContent>
                    </Card>
                    <Card className="border-blue-200 bg-blue-50/50">
                        <CardContent className="p-4">
                            <p className="text-xs text-blue-600 font-medium">Open</p>
                            <div className="text-2xl font-bold text-blue-700 mt-1">{open_tickets}</div>
                        </CardContent>
                    </Card>
                    <Card className="border-yellow-200 bg-yellow-50/50">
                        <CardContent className="p-4">
                            <p className="text-xs text-yellow-600 font-medium">In Progress</p>
                            <div className="text-2xl font-bold text-yellow-700 mt-1">{in_progress_tickets}</div>
                        </CardContent>
                    </Card>
                    <Card className="border-green-200 bg-green-50/50">
                        <CardContent className="p-4">
                            <p className="text-xs text-green-600 font-medium">Resolved</p>
                            <div className="text-2xl font-bold text-green-700 mt-1">{resolved_tickets}</div>
                        </CardContent>
                    </Card>
                    <Card className="border-indigo-200 bg-indigo-50/50">
                        <CardContent className="p-4">
                            <p className="text-xs text-indigo-600 font-medium">Total Hours</p>
                            <div className="text-2xl font-bold text-indigo-700 mt-1">{total_hours_spent.toFixed(1)}h</div>
                        </CardContent>
                    </Card>
                </div>

                {company.notes && (
                    <Card className="mb-8">
                        <CardContent className="p-4">
                            <p className="text-sm font-semibold text-gray-600 mb-1">Notes</p>
                            <p className="text-sm text-gray-700 whitespace-pre-wrap">{company.notes}</p>
                        </CardContent>
                    </Card>
                )}

                {/* Monthly Maintenance Reports */}
                <Card className="mb-8">
                    <CardHeader className="flex flex-row items-center justify-between">
                        <CardTitle className="text-lg flex items-center gap-2">
                            <Activity className="w-5 h-5 text-blue-600" />
                            Monthly Maintenance Reports
                        </CardTitle>
                        <div className="flex items-center gap-2">
                            <CsvExportButton
                                filename={`${company.name}-maintenance-reports`}
                                rows={maintenance_reports}
                                columns={[
                                    { label: 'Month', value: 'month_year' },
                                    { label: 'Maintenance Date', value: 'maintenance_date' },
                                    { label: 'Status', value: 'status' },
                                    { label: 'Time Spent (hours)', value: 'time_spent' },
                                    { label: 'Plugins', value: 'plugins_status' },
                                    { label: 'Security', value: 'security_status' },
                                    { label: 'Debug Log', value: 'debug_log_status' },
                                    { label: 'Layout', value: 'layout_status' },
                                    { label: 'Notes', value: 'general_notes' },
                                    { label: 'Completed By', value: row => row.updated_by_email || row.created_by_email },
                                ]}
                            />
                            <Button size="sm" className="bg-black hover:bg-gray-800" onClick={openCreateMaintenanceDialog}>
                                <Plus className="w-4 h-4 mr-2" />
                                Add Report
                            </Button>
                        </div>
                    </CardHeader>
                    <CardContent>
                        {maintenance_reports.length > 0 ? (
                            <div className="space-y-4">
                                {maintenance_reports.map((report) => (
                                    <div key={report.id} className="border rounded-lg p-4 bg-white shadow-sm hover:shadow-md transition-shadow">
                                        <div className="flex items-center justify-between mb-4 pb-2 border-b">
                                            <div>
                                                <h4 className="font-bold text-lg flex items-center gap-2">
                                                    <Calendar className="w-4 h-4 text-blue-600" />
                                                    {report.month_year}
                                                    {report.status === 'scheduled' && <Badge className="bg-blue-100 text-blue-700">Scheduled</Badge>}
                                                    {report.status === 'in_progress' && <Badge className="bg-amber-100 text-amber-700">In Progress</Badge>}
                                                    {report.status === 'completed' && <Badge className="bg-green-100 text-green-700"><CheckCircle2 className="w-3 h-3 mr-1" />Completed</Badge>}
                                                </h4>
                                                <span className="text-xs text-gray-500 block mt-1">
                                                    Maintenance date: {report.maintenance_date ? new Date(report.maintenance_date + 'T00:00:00').toLocaleDateString() : 'N/A'}
                                                </span>
                                                <span className="text-xs text-gray-500 block mt-1">
                                                    Checked by {report.created_by_email} on {new Date(report.created_at).toLocaleDateString()}
                                                </span>
                                                {report.updated_at && (
                                                    <span className="text-xs text-blue-600 block mt-0.5">Updated by {report.updated_by_email} on {new Date(report.updated_at).toLocaleDateString()}</span>
                                                )}
                                                <div className="mt-2">
                                                    <Badge variant="secondary" className="text-xs font-normal">
                                                        <Clock className="w-3 h-3 mr-1" />
                                                        {report.status === 'scheduled' ? 'Estimated' : 'Time Logged'}: {report.time_spent}h
                                                    </Badge>
                                                </div>
                                            </div>
                                            <div className="flex gap-2">
                                                <ClickUpTaskButton taskId={report.clickup_task_id} iconOnly className="h-8 w-8 p-0" />
                                                {report.status !== 'completed' && (
                                                    <Button size="sm" onClick={() => handleCompleteReport(report.id)} className="bg-green-600 hover:bg-green-700">
                                                        <CheckCircle2 className="w-4 h-4 mr-1" />
                                                        Complete
                                                    </Button>
                                                )}
                                                <Button variant="ghost" size="sm" onClick={() => handleEditReport(report)} className="text-blue-600 hover:text-blue-800 hover:bg-blue-50">
                                                    <Edit className="w-4 h-4" />
                                                </Button>
                                                <Button variant="ghost" size="sm" onClick={() => handleDeleteReport(report.id)} className="text-red-500 hover:text-red-700 hover:bg-red-50">
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div className="bg-gray-50 p-3 rounded-md">
                                                <div className="flex justify-between items-center mb-2">
                                                    <span className="font-medium text-sm flex items-center gap-2">
                                                        <FileText className="w-4 h-4 text-gray-500" /> Plugins
                                                    </span>
                                                    {getMaintenanceBadge(report.plugins_status)}
                                                </div>
                                                {report.plugins_notes && <p className="text-xs text-gray-600 pl-2 border-l-2 border-gray-300 italic">{report.plugins_notes}</p>}
                                            </div>

                                            <div className="bg-gray-50 p-3 rounded-md">
                                                <div className="flex justify-between items-center mb-2">
                                                    <span className="font-medium text-sm flex items-center gap-2">
                                                        <ShieldCheck className="w-4 h-4 text-gray-500" /> Security
                                                    </span>
                                                    {getMaintenanceBadge(report.security_status)}
                                                </div>
                                                {report.security_notes && <p className="text-xs text-gray-600 pl-2 border-l-2 border-gray-300 italic">{report.security_notes}</p>}
                                            </div>

                                            <div className="bg-gray-50 p-3 rounded-md">
                                                <div className="flex justify-between items-center mb-2">
                                                    <span className="font-medium text-sm flex items-center gap-2">
                                                        <Activity className="w-4 h-4 text-gray-500" /> Debug Log
                                                    </span>
                                                    {getMaintenanceBadge(report.debug_log_status)}
                                                </div>
                                                {report.debug_log_notes && <p className="text-xs text-gray-600 pl-2 border-l-2 border-gray-300 italic">{report.debug_log_notes}</p>}
                                            </div>

                                            <div className="bg-gray-50 p-3 rounded-md">
                                                <div className="flex justify-between items-center mb-2">
                                                    <span className="font-medium text-sm flex items-center gap-2">
                                                        <Layout className="w-4 h-4 text-gray-500" /> Layout
                                                    </span>
                                                    {getMaintenanceBadge(report.layout_status)}
                                                </div>
                                                {report.layout_notes && <p className="text-xs text-gray-600 pl-2 border-l-2 border-gray-300 italic">{report.layout_notes}</p>}
                                            </div>
                                        </div>

                                        {report.general_notes && (
                                            <div className="mt-4 pt-3 border-t">
                                                <span className="text-xs font-semibold text-gray-500 uppercase block mb-2">General Notes</span>
                                                <div className="bg-blue-50 p-3 rounded border border-blue-100 text-sm text-gray-700 whitespace-pre-wrap">
                                                    {report.general_notes}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg border border-dashed border-gray-200">
                                <Activity className="w-10 h-10 mx-auto mb-2 opacity-20" />
                                <p>No maintenance reports found.</p>
                                <Button variant="link" onClick={openCreateMaintenanceDialog}>Start first check</Button>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Tickets list */}
                <Card>
                    <CardHeader>
                        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                            <CardTitle className="text-lg flex items-center gap-2">
                                <BarChart3 className="w-5 h-5 text-gray-500" />
                                All Tickets ({tickets.length})
                            </CardTitle>

                            <Select value={sortBy} onValueChange={setSortBy}>
                                <SelectTrigger className="w-[180px] h-9">
                                    <SelectValue placeholder="Sort By..." />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="newest">Newest First</SelectItem>
                                    <SelectItem value="oldest">Oldest First</SelectItem>
                                    <SelectItem value="hours">Most Logged Hours</SelectItem>
                                    <SelectItem value="priority">Priority</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </CardHeader>
                    <CardContent>
                        {tickets.length === 0 ? (
                            <div className="text-center py-12 text-gray-400">
                                <Ticket className="w-10 h-10 mx-auto mb-2 opacity-30" />
                                <p>No tickets linked to this company yet or match selected filters.</p>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {[...tickets]
                                    .sort((a, b) => {
                                        // Sort by due_date (primary date for all tasks)
                                        const dateA = a.due_date || '';
                                        const dateB = b.due_date || '';
                                        if (sortBy === 'newest') return dateB.localeCompare(dateA);
                                        if (sortBy === 'oldest') return dateA.localeCompare(dateB);
                                        if (sortBy === 'hours') return (b.total_hours_spent || 0) - (a.total_hours_spent || 0);
                                        if (sortBy === 'priority') {
                                            const priorityWeight = { critical: 4, high: 3, medium: 2, low: 1 };
                                            return (priorityWeight[b.priority] || 0) - (priorityWeight[a.priority] || 0);
                                        }
                                        return 0;
                                    })
                                    .map(ticket => (
                                        <div
                                            key={ticket.id}
                                            className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer border border-transparent hover:border-gray-200"
                                            onClick={() => { setSelectedTicketId(ticket.id); setTicketDialogOpen(true); }}
                                        >
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 flex-wrap mb-1">
                                                    <span className="font-medium text-gray-900 truncate">{ticket.title}</span>
                                                    <Badge className={`${priorityColors[ticket.priority]} border text-xs`}>{ticket.priority}</Badge>
                                                    <Badge variant="outline" className="text-xs bg-gray-50 text-gray-600 truncate max-w-[150px]">
                                                        {ticket.problem_type}
                                                    </Badge>
                                                </div>
                                                <div className="flex items-center gap-4 text-xs text-gray-500">
                                                    <span>{ticket.created_by_email}</span>
                                                    {ticket.is_paid_support ? (
                                                        ticket.due_date
                                                            ? <span className="text-indigo-600 font-medium">Due: {new Date(ticket.due_date).toLocaleDateString()}</span>
                                                            : <span className="text-gray-400 italic">No due date</span>
                                                    ) : (
                                                        ticket.created_at && <span>Created: {new Date(ticket.created_at).toLocaleDateString()}</span>
                                                    )}
                                                    {ticket.total_hours_spent > 0 && (
                                                        <span className="flex items-center gap-1 text-orange-600 font-medium">
                                                            <Clock className="w-3 h-3" /> {ticket.total_hours_spent}h
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="ml-4 flex-shrink-0 flex items-center gap-2">
                                                <ClickUpTaskButton taskId={ticket.clickup_task_id} iconOnly className="h-8 w-8 p-0" />
                                                <Badge className={`${statusColors[ticket.status]} text-xs`}>{ticket.status?.replace('_', ' ')}</Badge>
                                            </div>
                                        </div>
                                    ))}
                            </div>
                        )}
                    </CardContent>
                </Card>
              </>)}

              {/* ══════ CLIENT INFO TAB ══════ */}
              {activeTab === 'client-info' && (
                <div className="space-y-6">
                  {/* Infrastructure Info */}
                  <Card>
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-lg flex items-center gap-2"><Server className="w-5 h-5 text-gray-500" />Infrastructure</CardTitle>
                        {!editingInfo ? (
                          <Button variant="outline" size="sm" onClick={startEditInfo} className="gap-1.5"><Edit className="w-3.5 h-3.5" />Edit</Button>
                        ) : (
                          <div className="flex gap-2">
                            <Button variant="outline" size="sm" onClick={() => setEditingInfo(false)} className="gap-1.5"><X className="w-3.5 h-3.5" />Cancel</Button>
                            <Button size="sm" onClick={handleSaveInfo} disabled={savingInfo} className="gap-1.5 bg-black hover:bg-gray-800">
                              {savingInfo ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}Save
                            </Button>
                          </div>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent>
                      {editingInfo ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-1.5">
                            <Label className="text-xs font-semibold text-gray-500 uppercase">Hosting Location</Label>
                            <InfraTagSearch value={infoForm.hosting_location} onChange={v => setInfoForm({...infoForm, hosting_location: v})} options={infraOptions.hosting_locations} placeholder="Search or type new..." />
                          </div>
                          <div className="space-y-1.5">
                            <Label className="text-xs font-semibold text-gray-500 uppercase">Domain Registrar</Label>
                            <InfraTagSearch value={infoForm.domain_registrar} onChange={v => setInfoForm({...infoForm, domain_registrar: v})} options={infraOptions.domain_registrars} placeholder="Search or type new..." />
                          </div>
                          <div className="space-y-1.5">
                            <Label className="text-xs font-semibold text-gray-500 uppercase">DNS Zone Editor</Label>
                            <InfraTagSearch value={infoForm.dns_zone_editor} onChange={v => setInfoForm({...infoForm, dns_zone_editor: v})} options={infraOptions.dns_zone_editors} placeholder="Search or type new..." />
                          </div>
                          <div className="space-y-1.5">
                            <Label className="text-xs font-semibold text-gray-500 uppercase">Company Tags</Label>
                            <div className="flex flex-wrap gap-1 mb-1">
                                {infoForm.tags?.map(tag => (
                                    <span key={tag} className="text-[10px] px-2 py-0.5 rounded bg-indigo-100 text-indigo-700 flex items-center gap-1">
                                        {tag}
                                        <button type="button" onClick={() => setInfoForm(prev => ({ ...prev, tags: prev.tags.filter(t => t !== tag) }))} className="hover:text-red-500"><X className="w-3 h-3"/></button>
                                    </span>
                                ))}
                            </div>
                            <Select onValueChange={(val) => {
                                if (val && !infoForm.tags?.includes(val)) {
                                    setInfoForm(prev => ({ ...prev, tags: [...(prev.tags || []), val] }));
                                }
                            }}>
                                <SelectTrigger className="h-9">
                                    <SelectValue placeholder="Add tag..." />
                                </SelectTrigger>
                                <SelectContent>
                                    {availableTags.filter(t => !infoForm.tags?.includes(t)).map(tag => (
                                        <SelectItem key={tag} value={tag}>{tag}</SelectItem>
                                    ))}
                                    {availableTags.filter(t => !infoForm.tags?.includes(t)).length === 0 && (
                                        <div className="p-2 text-xs text-gray-500 text-center">No more tags available</div>
                                    )}
                                </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-1.5">
                            <Label className="text-xs font-semibold text-gray-500 uppercase">Domain Expiry</Label>
                            <Input type="date" value={infoForm.domain_expiry} onChange={e => setInfoForm({...infoForm, domain_expiry: e.target.value})} />
                          </div>
                          <div className="space-y-1.5 md:col-span-2">
                            <Label className="text-xs font-semibold text-gray-500 uppercase">Delegate Access Info</Label>
                            <Textarea value={infoForm.delegate_access} onChange={e => setInfoForm({...infoForm, delegate_access: e.target.value})} placeholder="e.g. GoDaddy delegate email: admin@company.com" rows={2} />
                          </div>
                          <label className="md:col-span-2 flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3 cursor-pointer">
                            <Checkbox
                              checked={infoForm.exclude_from_maintenance}
                              onCheckedChange={checked => setInfoForm({ ...infoForm, exclude_from_maintenance: checked === true })}
                              className="mt-0.5"
                            />
                            <span>
                              <span className="block text-sm font-semibold text-amber-900">Exclude from website maintenance</span>
                              <span className="block text-xs text-amber-700 mt-0.5">This company will not appear in recurring monthly website maintenance.</span>
                            </span>
                          </label>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {company.exclude_from_maintenance && (
                            <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
                              <p className="text-sm font-semibold text-amber-900">Excluded from website maintenance</p>
                              <p className="text-xs text-amber-700 mt-0.5">Recurring monthly maintenance tasks are disabled for this company.</p>
                            </div>
                          )}
                          {/* Check if ANY infra data is set */}
                          {!company.hosting_location && !company.hosting && !company.domain_registrar && !company.dns_zone_editor && !company.domain_expiry && !company.delegate_access ? (
                            <div className="text-center py-8 bg-gray-50/50 rounded-xl border border-dashed border-gray-200">
                              <Server className="w-10 h-10 text-gray-200 mx-auto mb-3" />
                              <p className="text-sm font-medium text-gray-500">No infrastructure data yet</p>
                              <p className="text-xs text-gray-400 mt-1 max-w-[280px] mx-auto">Add hosting, domain, and DNS details to keep your team informed about this client's setup.</p>
                              <Button variant="outline" size="sm" onClick={startEditInfo} className="mt-4 gap-1.5 text-xs">
                                <Edit className="w-3.5 h-3.5" />Get Started
                              </Button>
                            </div>
                          ) : (
                            <>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                            {[
                              ['Hosting', company.hosting_location || company.hosting, Server, 'bg-emerald-50 border-emerald-200', 'text-emerald-600', 'bg-emerald-100 text-emerald-700'],
                              ['Registrar', company.domain_registrar, Globe, 'bg-blue-50 border-blue-200', 'text-blue-600', 'bg-blue-100 text-blue-700'],
                              ['DNS Zone', company.dns_zone_editor, Shield, 'bg-violet-50 border-violet-200', 'text-violet-600', 'bg-violet-100 text-violet-700'],
                              ['Expiry', company.domain_expiry ? new Date(company.domain_expiry).toLocaleDateString() : null, Calendar, 'bg-orange-50 border-orange-200', 'text-orange-600', 'bg-orange-100 text-orange-700'],
                            ].map(([label, val, Icon, bgCls, iconCls, badgeCls]) => (
                              <div key={label} className={`rounded-xl p-3.5 border ${val ? bgCls : 'bg-gray-50 border-gray-100'} transition-colors`}>
                                <div className="flex items-center gap-1.5 mb-2">
                                  <Icon className={`w-3.5 h-3.5 ${val ? iconCls : 'text-gray-400'}`} />
                                  <span className={`text-[10px] font-bold uppercase tracking-wider ${val ? iconCls : 'text-gray-400'}`}>{label}</span>
                                </div>
                                {val ? (
                                  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${badgeCls}`}>{val}</span>
                                ) : (
                                  <span className="text-xs text-gray-300 italic">—</span>
                                )}
                                {label === 'Expiry' && val && (() => {
                                  const days = Math.ceil((new Date(company.domain_expiry) - new Date()) / 86400000);
                                  if (days < 0) return <div className="mt-1.5"><span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-medium">⚠ Expired</span></div>;
                                  if (days <= 30) return <div className="mt-1.5"><span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">⚠ {days}d left</span></div>;
                                  return <div className="mt-1.5"><span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">✓ {days}d left</span></div>;
                                })()}
                              </div>
                            ))}
                          </div>
                          {(company.delegate_access) && (
                            <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
                              <div className="flex items-center gap-2 mb-2">
                                <Key className="w-4 h-4 text-amber-600" />
                                <span className="text-xs font-bold text-amber-600 uppercase tracking-wider">Delegate Access</span>
                              </div>
                              <p className="text-sm text-gray-800 whitespace-pre-wrap bg-white/60 rounded-lg p-3 border border-amber-100">{company.delegate_access}</p>
                            </div>
                          )}
                            </>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Credentials Vault */}
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg flex items-center gap-2"><Key className="w-5 h-5 text-gray-500" />Credentials</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {editingInfo ? (
                        <div className="space-y-3">
                          {infoForm.credentials.map((cred, idx) => (
                            <div key={cred.id || idx} className="flex gap-2 items-start">
                              <Input placeholder="Label (e.g. cPanel User)" value={cred.label} onChange={e => updateCredential(idx, 'label', e.target.value)} className="flex-1 h-9 text-sm" />
                              <Input placeholder="Value" value={cred.value} onChange={e => updateCredential(idx, 'value', e.target.value)} className="flex-1 h-9 text-sm" />
                              <Button variant="ghost" size="sm" onClick={() => removeCredential(idx)} className="h-9 w-9 p-0 text-red-400 hover:text-red-600 hover:bg-red-50"><Trash2 className="w-4 h-4" /></Button>
                            </div>
                          ))}
                          <Button variant="outline" size="sm" onClick={addCredential} className="gap-1.5 text-xs"><Plus className="w-3.5 h-3.5" />Add Credential</Button>
                        </div>
                      ) : (
                        (company.credentials || []).length === 0 ? (
                          <p className="text-sm text-gray-400 italic py-4 text-center">No credentials stored. Click Edit on Infrastructure to add.</p>
                        ) : (
                          <div className="space-y-2">
                            {(company.credentials || []).map(cred => (
                              <div key={cred.id} className="flex items-center justify-between bg-gray-50 rounded-lg p-3 border border-gray-100 group">
                                <div>
                                  <span className="text-xs font-semibold text-gray-500 uppercase">{cred.label}</span>
                                  <p className="text-sm font-mono text-gray-800 mt-0.5">{visibleCreds.has(cred.id) ? cred.value : '••••••••'}</p>
                                </div>
                                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                  <Button variant="ghost" size="sm" onClick={() => toggleCredVisible(cred.id)} className="h-7 w-7 p-0">
                                    {visibleCreds.has(cred.id) ? <EyeOff className="w-3.5 h-3.5 text-gray-500" /> : <Eye className="w-3.5 h-3.5 text-gray-500" />}
                                  </Button>
                                  <Button variant="ghost" size="sm" onClick={() => copyToClipboard(cred.value)} className="h-7 w-7 p-0"><Copy className="w-3.5 h-3.5 text-gray-500" /></Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )
                      )}
                    </CardContent>
                  </Card>

                  {/* Knowledge Base */}
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg flex items-center gap-2"><MessageSquare className="w-5 h-5 text-gray-500" />Knowledge Base</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="flex gap-2">
                        <Textarea value={noteText} onChange={e => setNoteText(e.target.value)} placeholder="Add a note about this client..." rows={2} className="flex-1" />
                        <Button onClick={handleAddNote} disabled={addingNote || !noteText.trim()} className="bg-black hover:bg-gray-800 self-end px-4">
                          {addingNote ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Post'}
                        </Button>
                      </div>
                      {(company.knowledge_notes || []).length === 0 ? (
                        <p className="text-sm text-gray-400 italic text-center py-4">No notes yet. Add the first one above.</p>
                      ) : (
                        <div className="space-y-3">
                          {[...(company.knowledge_notes || [])].reverse().map(note => (
                            <div key={note.id} className="bg-gray-50 rounded-lg p-4 border border-gray-100 group">
                              <p className="text-sm text-gray-800 whitespace-pre-wrap">{note.text}</p>
                              <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100">
                                <span className="text-xs text-gray-400">{note.created_by_email} · {new Date(note.created_at).toLocaleString()}</span>
                                <Button variant="ghost" size="sm" onClick={() => handleDeleteNote(note.id)}
                                  className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-red-500 hover:bg-red-50">
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Activity Log */}
                  <Card>
                    <CardHeader className="pb-3 border-b border-gray-50">
                        <CardTitle className="text-lg flex items-center gap-2"><History className="w-5 h-5 text-gray-500" />Activity Log</CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                        {auditLogs.length === 0 ? (
                            <p className="text-sm text-gray-400 italic text-center py-6">No activity recorded yet.</p>
                        ) : (
                            <div className="divide-y divide-gray-50 max-h-[400px] overflow-y-auto">
                                {auditLogs.map(log => (
                                    <div key={log.id} className="px-6 py-3 hover:bg-gray-50 transition-colors">
                                        <div className="flex items-center gap-2">
                                            <span className="text-xs font-medium text-gray-700">{log.user_email}</span>
                                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                                                log.action === 'update_info' ? 'bg-blue-100 text-blue-700' :
                                                log.action === 'add_note' ? 'bg-green-100 text-green-700' :
                                                log.action === 'delete_note' ? 'bg-red-100 text-red-700' :
                                                'bg-gray-100 text-gray-600'
                                            }`}>{log.action.replace(/_/g, ' ')}</span>
                                            <span className="text-[10px] text-gray-400 ml-auto">{new Date(log.created_at).toLocaleString()}</span>
                                        </div>
                                        <p className="text-[11px] text-gray-500 mt-0.5">{log.details}</p>
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                  </Card>
                </div>
              )}
            </main>

            <TicketDialog
                ticketId={selectedTicketId}
                open={ticketDialogOpen}
                onOpenChange={setTicketDialogOpen}
                user={user}
                companies={[company]}
                onUpdate={fetchData}
            />

            {/* Maintenance Report Dialog */}
            <Dialog open={maintenanceDialogOpen} onOpenChange={setMaintenanceDialogOpen}>
                <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Create or Edit Maintenance Report</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-6 py-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Maintenance Date</Label>
                                <Input
                                    type="date"
                                    value={newReport.maintenance_date}
                                    onChange={(e) => setNewReport({ ...newReport, maintenance_date: e.target.value })}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Time Spent (Hours)</Label>
                                <Input
                                    type="number"
                                    step="0.5"
                                    min="0"
                                    value={newReport.time_spent}
                                    onChange={(e) => setNewReport({ ...newReport, time_spent: parseFloat(e.target.value) || 0 })}
                                />
                            </div>
                        </div>

                        <div className="space-y-3 p-4 bg-gray-50 rounded-lg border">
                            <div className="flex items-center justify-between">
                                <Label className="flex items-center gap-2 text-base font-semibold">
                                    <FileText className="w-4 h-4" /> Plugin Updates
                                </Label>
                                <Select
                                    value={newReport.plugins_status}
                                    onValueChange={(val) => setNewReport({ ...newReport, plugins_status: val })}
                                >
                                    <SelectTrigger className="w-[140px]">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="healthy">Healthy</SelectItem>
                                        <SelectItem value="issues_found">Issues Found</SelectItem>
                                        <SelectItem value="critical">Critical</SelectItem>
                                        <SelectItem value="not_checked">Not Checked</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <Textarea
                                placeholder="Notes on plugin updates..."
                                value={newReport.plugins_notes}
                                onChange={(e) => setNewReport({ ...newReport, plugins_notes: e.target.value })}
                                className="h-20 bg-white"
                            />
                        </div>

                        <div className="space-y-3 p-4 bg-gray-50 rounded-lg border">
                            <div className="flex items-center justify-between">
                                <Label className="flex items-center gap-2 text-base font-semibold">
                                    <ShieldCheck className="w-4 h-4" /> Security Log Activity
                                </Label>
                                <Select
                                    value={newReport.security_status}
                                    onValueChange={(val) => setNewReport({ ...newReport, security_status: val })}
                                >
                                    <SelectTrigger className="w-[140px]">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="healthy">Healthy</SelectItem>
                                        <SelectItem value="issues_found">Issues Found</SelectItem>
                                        <SelectItem value="critical">Critical</SelectItem>
                                        <SelectItem value="not_checked">Not Checked</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <Textarea
                                placeholder="Notes on security logs..."
                                value={newReport.security_notes}
                                onChange={(e) => setNewReport({ ...newReport, security_notes: e.target.value })}
                                className="h-20 bg-white"
                            />
                        </div>

                        <div className="space-y-3 p-4 bg-gray-50 rounded-lg border">
                            <div className="flex items-center justify-between">
                                <Label className="flex items-center gap-2 text-base font-semibold">
                                    <Activity className="w-4 h-4" /> Debug Logs
                                </Label>
                                <Select
                                    value={newReport.debug_log_status}
                                    onValueChange={(val) => setNewReport({ ...newReport, debug_log_status: val })}
                                >
                                    <SelectTrigger className="w-[140px]">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="healthy">Healthy</SelectItem>
                                        <SelectItem value="issues_found">Issues Found</SelectItem>
                                        <SelectItem value="critical">Critical</SelectItem>
                                        <SelectItem value="not_checked">Not Checked</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <Textarea
                                placeholder="Notes on PHP errors/debug.log..."
                                value={newReport.debug_log_notes}
                                onChange={(e) => setNewReport({ ...newReport, debug_log_notes: e.target.value })}
                                className="h-20 bg-white"
                            />
                        </div>

                        <div className="space-y-3 p-4 bg-gray-50 rounded-lg border">
                            <div className="flex items-center justify-between">
                                <Label className="flex items-center gap-2 text-base font-semibold">
                                    <Layout className="w-4 h-4" /> Broken Layout (Mobile/Desktop)
                                </Label>
                                <Select
                                    value={newReport.layout_status}
                                    onValueChange={(val) => setNewReport({ ...newReport, layout_status: val })}
                                >
                                    <SelectTrigger className="w-[140px]">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="healthy">Healthy</SelectItem>
                                        <SelectItem value="issues_found">Issues Found</SelectItem>
                                        <SelectItem value="critical">Critical</SelectItem>
                                        <SelectItem value="not_checked">Not Checked</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <Textarea
                                placeholder="Notes on layout check..."
                                value={newReport.layout_notes}
                                onChange={(e) => setNewReport({ ...newReport, layout_notes: e.target.value })}
                                className="h-20 bg-white"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label>General Notes</Label>
                            <Textarea
                                placeholder="Anything else to record..."
                                value={newReport.general_notes}
                                onChange={(e) => setNewReport({ ...newReport, general_notes: e.target.value })}
                                rows={3}
                            />
                        </div>
                    </div>
                    <div className="flex justify-end gap-2">
                        <Button variant="ghost" onClick={() => setMaintenanceDialogOpen(false)}>Cancel</Button>
                        <Button onClick={handleCreateReport} className="bg-black hover:bg-gray-800">Save as In Progress</Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Create Task Dialog */}
            <Dialog open={createTaskOpen} onOpenChange={setCreateTaskOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Building2 className="w-4 h-4" /> New Task for {company?.name}
                        </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreateTask} className="space-y-4">
                        <div className="space-y-2">
                            <Label>Problem Type</Label>
                            <ProblemTypeSearch
                                value={newTask.problem_type}
                                onChange={(ptValue) => setNewTask({ ...newTask, problem_type: ptValue })}
                                placeholder="Select problem type..."
                                allowCreate={true}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Title <span className="text-red-500">*</span></Label>
                            <Input
                                placeholder="Brief description of the task"
                                value={newTask.title}
                                onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                                required
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Description</Label>
                            <Textarea
                                placeholder="Detailed information..."
                                value={newTask.description}
                                onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                                rows={3}
                                required
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Requested By <span className="text-gray-400 font-normal">(optional)</span></Label>
                            <RequesterSearch
                                value={newTask.requested_by || null}
                                onChange={id => setNewTask({ ...newTask, requested_by: id || '' })}
                                placeholder="Select requester..."
                            />
                            <p className="text-xs text-gray-500">Email notifications use the selected requester’s saved email address.</p>
                        </div>
                        <div className="space-y-2">
                            <Label>Create on behalf of / ClickUp assignee <span className="text-gray-400 font-normal">(optional)</span></Label>
                            <Select
                                value={newTask.assigned_to || 'unassigned'}
                                onValueChange={value => setNewTask({ ...newTask, assigned_to: value === 'unassigned' ? '' : value })}
                            >
                                <SelectTrigger><SelectValue placeholder="Assign to myself" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="unassigned">Assign to myself</SelectItem>
                                    {taskAssignees.map(person => (
                                        <SelectItem key={person.id} value={person.id}>{person.name || person.email} · {person.email}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <p className="text-xs text-gray-500">This person becomes the app owner and ClickUp assignee. Requested By only controls requester tracking and emails.</p>
                        </div>
                        <div className="space-y-2">
                            <Label>Due Date <span className="text-red-500">*</span></Label>
                            <Input
                                type="date"
                                value={newTask.due_date}
                                onChange={(e) => setNewTask({ ...newTask, due_date: e.target.value })}
                                required
                            />
                        </div>
                        <Button type="submit" className="w-full bg-black hover:bg-gray-800" disabled={submittingTask}>
                            {submittingTask ? 'Creating...' : 'Create Task'}
                        </Button>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default CompanyDetail;
